import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Link2, Plug, RefreshCw, Loader2, ExternalLink, Check, AlertCircle,
  ShieldCheck, Search, X, Eye, Rocket,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ebayAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * eBay Listing Panel — connection state + Save-as-Draft flow.
 *
 * Renders ONLY for the Founder (admin) on the eBay marketplace tab. Three states:
 *  1. Not connected     → shows Connect button (opens popup)
 *  2. Connected, no draft → shows "Save as Draft on eBay" button
 *  3. Connected, has draft → shows offer id + Seller Hub link + Update button
 */
export default function EbayListingPanel({ product, onUpdate }) {
  const { isEbayUser } = useAuth();
  const [status, setStatus] = useState(null); // null = loading, {} = response
  const [policies, setPolicies] = useState(null);
  const [loadingPolicies, setLoadingPolicies] = useState(false);
  const [policiesError, setPoliciesError] = useState(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [publishing, setPublishing] = useState(false);

  // Dialog state
  const [paymentPolicyId, setPaymentPolicyId] = useState('');
  const [fulfillmentPolicyId, setFulfillmentPolicyId] = useState('');
  const [returnPolicyId, setReturnPolicyId] = useState('');
  const [categoryQuery, setCategoryQuery] = useState('');
  const [categorySuggestions, setCategorySuggestions] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [categoryName, setCategoryName] = useState('');
  const [searchingCategory, setSearchingCategory] = useState(false);

  // Allow-listed eBay users — bail early for everyone else
  const founderView = isEbayUser?.();

  const loadStatus = useCallback(async () => {
    if (!founderView) return;
    try {
      const res = await ebayAPI.oauthStatus();
      setStatus(res.data);
    } catch (err) {
      setStatus({ connected: false, error: err.response?.data?.detail || 'Status check failed' });
    }
  }, [founderView]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Listen for OAuth popup completion
  useEffect(() => {
    if (!founderView) return undefined;
    const onMsg = (e) => {
      const data = e?.data;
      if (data && data.type === 'ebay-oauth') {
        if (data.success) {
          toast.success(data.message || 'eBay connected');
          loadStatus();
        } else {
          toast.error(data.message || 'eBay connection failed');
        }
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [founderView, loadStatus]);

  const handleConnect = async () => {
    try {
      const res = await ebayAPI.oauthStart();
      const w = 600, h = 720;
      const left = (window.screen.width - w) / 2;
      const top = (window.screen.height - h) / 2;
      window.open(
        res.data.auth_url,
        'ebay-oauth',
        `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start eBay OAuth');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect eBay? You will need to re-authorize to push new drafts.')) return;
    try {
      await ebayAPI.oauthDisconnect();
      setStatus({ connected: false });
      toast.success('eBay disconnected');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Disconnect failed');
    }
  };

  const openSaveDialog = async () => {
    setShowSaveDialog(true);
    // Pre-select tab based on prior listing type (or default to fixed for new)
    setActiveTab(product?.ebay_listing_type === 'variation' ? 'variation' : 'fixed');
    setPaymentPolicyId(product?.ebay_payment_policy_id || '');
    setFulfillmentPolicyId(product?.ebay_fulfillment_policy_id || '');
    setReturnPolicyId(product?.ebay_return_policy_id || '');
    setCategoryId(product?.ebay_category_id || '');
    setCategoryName(product?.ebay_category_name || '');

    // Fixed defaults
    setFixedMetal(product?.ebay_fixed_metal || '');
    setFixedPurity(product?.ebay_fixed_metal_purity || '');
    setFixedCarat(product?.ebay_fixed_carat ? String(product.ebay_fixed_carat) : '');
    setFixedPriceOverride('');

    // Auto-extract fixed attributes if not previously set
    if (!product?.ebay_fixed_metal) {
      try {
        const res = await ebayAPI.extractFixed(product.id);
        const ex = res.data || {};
        setExtractedFixed(ex);
        if (ex.metal) setFixedMetal(ex.metal);
        if (ex.metal_purity) setFixedPurity(ex.metal_purity);
        if (ex.carat) setFixedCarat(String(ex.carat));
      } catch (err) {
        // silent — user can fill manually
      }
    }

    // Auto-load policies
    if (!policies) {
      try {
        setLoadingPolicies(true);
        setPoliciesError(null);
        const res = await ebayAPI.getPolicies();
        setPolicies(res.data);
      } catch (err) {
        setPoliciesError(err.response?.data?.detail || 'Failed to load policies');
      } finally {
        setLoadingPolicies(false);
      }
    }

    // Auto-suggest category from best title
    const seed = product?.ebay_best_title || product?.best_title || product?.name;
    if (seed && !categoryId) {
      setCategoryQuery(seed.split(' ').slice(0, 5).join(' '));
    }
  };

  const handleSearchCategory = async () => {
    const q = (categoryQuery || '').trim();
    if (q.length < 2) return;
    try {
      setSearchingCategory(true);
      const res = await ebayAPI.categorySuggestions(q);
      const suggestions = res.data.suggestions || [];
      setCategorySuggestions(suggestions);
      if (suggestions.length === 0) {
        toast.info('No matches from eBay. Try simpler keywords or use the quick picks.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Category search failed');
    } finally {
      setSearchingCategory(false);
    }
  };

  // Quick-pick categories for TheLocalJewel — verified eBay US category IDs
  const QUICK_CATEGORIES = [
    { id: '164309', name: 'Engagement Rings', breadcrumb: 'Jewelry & Watches > Engagement & Wedding' },
    { id: '92852', name: 'Wedding Bands', breadcrumb: 'Jewelry & Watches > Engagement & Wedding' },
    { id: '67723', name: 'Fine Rings (Diamond)', breadcrumb: 'Jewelry & Watches > Fine Jewelry > Fine Rings' },
    { id: '50637', name: 'Earrings', breadcrumb: 'Jewelry & Watches > Fine Jewelry > Fine Earrings' },
    { id: '164331', name: 'Necklaces & Pendants', breadcrumb: 'Jewelry & Watches > Fine Jewelry > Fine Necklaces & Pendants' },
    { id: '10976', name: 'Bracelets', breadcrumb: 'Jewelry & Watches > Fine Jewelry > Fine Bracelets' },
  ];

  const pickQuickCategory = (cat) => {
    setCategoryId(cat.id);
    setCategoryName(cat.name);
    setCategorySuggestions([]);
  };

  const handleVerifyOnEbay = async () => {
    try {
      setVerifying(true);
      setVerifyResult(null);
      const res = await ebayAPI.draftStatus(product.id);
      setVerifyResult(res.data);
      toast.success(`Verified — ${res.data.variation_count} variations live on eBay`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const handlePublish = async () => {
    if (!window.confirm(
      'Publish this listing LIVE on eBay? It will become publicly searchable and listing fees will apply per eBay policy. Continue?'
    )) return;
    try {
      setPublishing(true);
      const res = await ebayAPI.publish(product.id);
      toast.success('Listing published live on eBay');
      onUpdate({
        ...product,
        ebay_status: 'published',
        ebay_listing_id: res.data.listing_id,
        ebay_live_url: res.data.live_url,
      });
      if (res.data.warnings?.length) {
        toast.warning(`eBay warning: ${res.data.warnings[0]}`);
      }
      // open the live listing in a new tab
      window.open(res.data.live_url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [savingLocation, setSavingLocation] = useState(false);
  const [locName, setLocName] = useState('TLJ Main Warehouse');
  const [locAddress1, setLocAddress1] = useState('');
  const [locCity, setLocCity] = useState('');
  const [locState, setLocState] = useState('');
  const [locPostal, setLocPostal] = useState('');
  const [locCountry, setLocCountry] = useState('IN');

  // Save Dialog tabs
  const [activeTab, setActiveTab] = useState('fixed');
  // Fixed-listing state
  const [fixedMetal, setFixedMetal] = useState('');
  const [fixedPurity, setFixedPurity] = useState('');
  const [fixedCarat, setFixedCarat] = useState('');
  const [fixedPriceOverride, setFixedPriceOverride] = useState('');
  const [extractedFixed, setExtractedFixed] = useState(null);

  const handleSaveLocation = async () => {
    const missing = [];
    if (!locName) missing.push('Name');
    if (!locAddress1) missing.push('Address');
    if (!locCity) missing.push('City');
    if (!locPostal) missing.push('Postal code');
    if (!locCountry) missing.push('Country');
    if (missing.length) { toast.error('Missing: ' + missing.join(', ')); return; }
    try {
      setSavingLocation(true);
      const res = await ebayAPI.createLocation({
        name: locName,
        address_line1: locAddress1,
        city: locCity,
        state_or_province: locState,
        postal_code: locPostal,
        country: locCountry,
      });
      toast.success('Shipping location saved');
      setShowLocationDialog(false);
      // Refresh status so the green pill shows
      setStatus((prev) => ({
        ...prev,
        default_location_key: res.data.merchant_location_key,
        default_location_country: res.data.country,
        default_location_name: locName,
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Location save failed');
    } finally {
      setSavingLocation(false);
    }
  };

  // Auto-compute the fixed price preview from the pricing table (mirrors backend)
  const computeFixedPrice = useCallback(() => {
    if (!fixedMetal || !fixedPurity || !fixedCarat) return null;
    const c = Number(fixedCarat);
    if (!c) return null;
    const sideCt = Number(product?.setting_stone_carat_weight || 0);
    const metalCostMap = { '10k': 350, '14k': 420, '18k': 600, '925': 280, '950': 750 };
    const cost = metalCostMap[fixedPurity];
    if (!cost) return null;
    const subtotal = cost + c * 150 + sideCt * 120 + 150 + 50 + 700;
    return Math.round(subtotal * 1.13);
  }, [fixedMetal, fixedPurity, fixedCarat, product?.setting_stone_carat_weight]);

  const handleSaveDraftFixed = async () => {
    const missing = [];
    if (!categoryId) missing.push('Category');
    if (!paymentPolicyId) missing.push('Payment policy');
    if (!fulfillmentPolicyId) missing.push('Shipping policy');
    if (!returnPolicyId) missing.push('Return policy');
    if (!fixedMetal) missing.push('Metal');
    if (!fixedPurity) missing.push('Metal Purity');
    if (!fixedCarat) missing.push('Carat');
    if (missing.length) { toast.error('Missing: ' + missing.join(', ')); return; }
    try {
      setSaving(true);
      const payload = {
        category_id: categoryId,
        category_name: categoryName,
        payment_policy_id: paymentPolicyId,
        fulfillment_policy_id: fulfillmentPolicyId,
        return_policy_id: returnPolicyId,
        metal: fixedMetal,
        metal_purity: fixedPurity,
        carat: Number(fixedCarat),
      };
      if (fixedPriceOverride) payload.price = Number(fixedPriceOverride);
      const res = await ebayAPI.saveDraftFixed(product.id, payload);
      toast.success(`Saved on eBay — Fixed listing @ $${Number(res.data.price).toLocaleString()}`);
      onUpdate({
        ...product,
        ebay_sku: res.data.sku,
        ebay_offer_id: res.data.offer_id,
        ebay_env: res.data.env,
        ebay_status: 'draft',
        ebay_listing_type: 'fixed',
        ebay_seller_hub_url: res.data.seller_hub_url,
        ebay_category_id: categoryId,
        ebay_category_name: categoryName,
        ebay_payment_policy_id: paymentPolicyId,
        ebay_fulfillment_policy_id: fulfillmentPolicyId,
        ebay_return_policy_id: returnPolicyId,
        ebay_price: res.data.price,
        ebay_fixed_metal: fixedMetal,
        ebay_fixed_metal_purity: fixedPurity,
        ebay_fixed_carat: Number(fixedCarat),
        ebay_aspects_common: res.data.common_aspects,
        // Clear any prior variation fields
        ebay_group_sku: null,
        ebay_variation_count: 0,
      });
      setShowSaveDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save draft failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    const missing = [];
    if (!categoryId) missing.push('Category');
    if (!paymentPolicyId) missing.push('Payment policy');
    if (!fulfillmentPolicyId) missing.push('Shipping policy');
    if (!returnPolicyId) missing.push('Return policy');
    if (missing.length) {
      toast.error('Missing: ' + missing.join(', '));
      return;
    }
    try {
      setSaving(true);
      const res = await ebayAPI.saveDraftWithVariations(product.id, {
        category_id: categoryId,
        category_name: categoryName,
        payment_policy_id: paymentPolicyId,
        fulfillment_policy_id: fulfillmentPolicyId,
        return_policy_id: returnPolicyId,
      });
      toast.success(`Saved on eBay — ${res.data.variation_count} variations`);
      onUpdate({
        ...product,
        ebay_group_sku: res.data.group_sku,
        ebay_variation_count: res.data.variation_count,
        ebay_env: res.data.env,
        ebay_status: 'draft',
        ebay_listing_type: 'variation',
        ebay_seller_hub_url: res.data.seller_hub_url,
        ebay_category_id: categoryId,
        ebay_category_name: categoryName,
        ebay_payment_policy_id: paymentPolicyId,
        ebay_fulfillment_policy_id: fulfillmentPolicyId,
        ebay_return_policy_id: returnPolicyId,
        ebay_aspects_common: res.data.common_aspects,
      });
      setShowSaveDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save draft failed');
    } finally {
      setSaving(false);
    }
  };

  if (!founderView) return null;
  if (status === null) {
    return (
      <div className="bg-white border border-zinc-200 rounded-xl p-5 text-xs text-zinc-500 inline-flex items-center gap-2">
        <Loader2 size={12} className="animate-spin" /> Checking eBay connection…
      </div>
    );
  }

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-3" data-testid="ebay-listing-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-base font-semibold text-zinc-900 inline-flex items-center gap-2">
            <Plug size={15} className="text-blue-600" /> Push to eBay
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Create a draft listing on eBay using the title, description, images, and price below.
          </p>
        </div>
        {status.connected ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-medium"
              data-testid="ebay-connection-pill"
            >
              <ShieldCheck size={11} /> Connected
              {status.seller_username ? ` · ${status.seller_username}` : ''}
              {status.env_name === 'sandbox' ? ' · SANDBOX' : ''}
            </span>
            <Button
              variant="outline" size="sm"
              onClick={handleDisconnect}
              className="border-zinc-200 text-zinc-600 hover:text-red-600"
              data-testid="ebay-disconnect-btn"
            >
              Disconnect
            </Button>
          </div>
        ) : (
          <Button
            onClick={handleConnect}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="ebay-connect-btn"
          >
            <Link2 size={13} className="mr-1.5" /> Connect eBay
          </Button>
        )}
      </div>

      {status.connected && !status.default_location_key && (
        <div className="bg-amber-50/40 border border-amber-100 rounded-md p-3 text-[12px] text-amber-900 leading-relaxed space-y-2" data-testid="ebay-location-setup-prompt">
          <div className="flex items-start gap-2">
            <AlertCircle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-semibold">Shipping location required</div>
              <div className="text-amber-900/80">
                eBay needs a registered warehouse address before you can save drafts or publish.
                Set it up once and we'll auto-attach it to every listing.
              </div>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => setShowLocationDialog(true)}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="ebay-setup-location-btn"
          >
            Setup shipping location
          </Button>
        </div>
      )}

      {status.connected && status.default_location_key && (
        <div className="flex items-center gap-2 text-[11px] text-zinc-500 px-1">
          <ShieldCheck size={11} className="text-emerald-600" />
          Ship-from: {status.default_location_name || status.default_location_key}
          {status.default_location_country ? ` · ${status.default_location_country}` : ''}
          <button
            className="text-blue-600 hover:text-blue-800 ml-auto"
            onClick={() => setShowLocationDialog(true)}
            data-testid="ebay-edit-location-btn"
          >
            Change
          </button>
        </div>
      )}

      {!status.connected && (
        <div className="bg-blue-50/50 border border-blue-100 rounded-md p-3 text-[12px] text-blue-900 leading-relaxed">
          Connect your eBay seller account to push drafts directly from this hub.
          You will be redirected to eBay to authorize TheLocalJewel Listing Manager
          (you can revoke access from your eBay account anytime).
        </div>
      )}

      {status.connected && (
        <div className="space-y-3">
          {(product.ebay_group_sku || product.ebay_offer_id) ? (
            <div className="bg-emerald-50/40 border border-emerald-100 rounded-md p-3 space-y-2" data-testid="ebay-existing-draft">
              <div className="flex items-center gap-2 text-emerald-900 text-[12px] font-medium">
                <Check size={13} className="text-emerald-700" />
                {product.ebay_status === 'published' ? 'Listing published live on eBay' : 'Draft saved on eBay'}
                {product.ebay_listing_type === 'fixed' ? (
                  <span className="ml-auto px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full text-[10px] uppercase tracking-wider font-semibold">
                    Fixed · {product.ebay_fixed_metal_purity?.toUpperCase()} {product.ebay_fixed_metal} · {Number(product.ebay_fixed_carat || 0).toFixed(2)} CT · ${Number(product.ebay_price || 0).toLocaleString()}
                  </span>
                ) : product.ebay_variation_count ? (
                  <span className="ml-auto px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full text-[10px] uppercase tracking-wider font-semibold">
                    {product.ebay_variation_count} variations
                  </span>
                ) : null}
              </div>
              <div className="text-[11px] text-emerald-900/70 font-mono break-all">
                {product.ebay_group_sku
                  ? <>Group SKU: {product.ebay_group_sku}</>
                  : <>Offer ID: {product.ebay_offer_id} · SKU: {product.ebay_sku}</>}
                {product.ebay_listing_id && (
                  <div className="mt-0.5">Listing ID: {product.ebay_listing_id}</div>
                )}
              </div>

              {product.ebay_status !== 'published' && (
                <div className="bg-amber-50/40 border border-amber-100 rounded-md p-2 text-[11px] text-amber-900 leading-snug">
                  <span className="font-semibold">Note:</span> API-created drafts don't appear in eBay's{' '}
                  <span className="font-mono">/sh/lst/drafts</span> tab — they live in eBay's Inventory area until published.
                  Use <span className="font-semibold">Verify on eBay</span> to confirm or{' '}
                  <span className="font-semibold">Publish Live</span> to make it visible.
                </div>
              )}

              <div className="flex items-center gap-2 flex-wrap pt-1">
                {product.ebay_status === 'published' && product.ebay_live_url && (
                  <a
                    href={product.ebay_live_url}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] text-emerald-700 hover:text-emerald-900 font-semibold"
                    data-testid="ebay-live-listing-link"
                  >
                    <ExternalLink size={11} /> View live listing
                  </a>
                )}

                <Button
                  variant="outline" size="sm"
                  onClick={handleVerifyOnEbay}
                  disabled={verifying}
                  className="border-zinc-200 text-zinc-700"
                  data-testid="ebay-verify-btn"
                >
                  {verifying
                    ? <><Loader2 size={11} className="mr-1.5 animate-spin" /> Verifying…</>
                    : <><Eye size={11} className="mr-1.5" /> Verify on eBay</>}
                </Button>

                {product.ebay_status !== 'published' && (
                  <Button
                    size="sm"
                    onClick={handlePublish}
                    disabled={publishing}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="ebay-publish-btn"
                  >
                    {publishing
                      ? <><Loader2 size={11} className="mr-1.5 animate-spin" /> Publishing…</>
                      : <><Rocket size={11} className="mr-1.5" /> Publish Live</>}
                  </Button>
                )}

                <Button
                  variant="outline" size="sm"
                  onClick={openSaveDialog}
                  className="border-zinc-200 text-zinc-700 ml-auto"
                  data-testid="ebay-update-draft-btn"
                >
                  <RefreshCw size={11} className="mr-1.5" /> Update draft
                </Button>
              </div>

              {/* Verify result panel */}
              {verifyResult && (
                <div className="bg-white border border-emerald-100 rounded-md p-2.5 text-[11px] space-y-1.5 mt-2" data-testid="ebay-verify-result">
                  <div className="font-semibold text-emerald-900">✓ Confirmed on eBay</div>
                  <div className="text-zinc-700">Title: <span className="font-medium">{verifyResult.title}</span></div>
                  <div className="text-zinc-700">Variations on eBay: <span className="font-medium">{verifyResult.variation_count}</span></div>
                  <div className="text-zinc-700">Image count: <span className="font-medium">{verifyResult.image_count}</span></div>
                  {verifyResult.aspects && (
                    <div className="text-zinc-700">
                      Aspects: <span className="font-medium">{Object.keys(verifyResult.aspects).length}</span> filled
                      {verifyResult.aspects.Brand ? <> · Brand: {verifyResult.aspects.Brand?.[0]}</> : null}
                      {verifyResult.aspects['Diamond Color Grade'] ? <> · Color: {verifyResult.aspects['Diamond Color Grade']?.[0]}</> : null}
                      {verifyResult.aspects['Diamond Clarity Grade'] ? <> · Clarity: {verifyResult.aspects['Diamond Clarity Grade']?.[0]}</> : null}
                    </div>
                  )}
                  {verifyResult.sample_offers?.length > 0 && (
                    <div className="text-zinc-700">
                      Sample prices:{' '}
                      {verifyResult.sample_offers.map((o, i) => (
                        <span key={o.offer_id || i} className="font-medium mr-2">${o.price}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <Button
              onClick={openSaveDialog}
              className="bg-blue-600 hover:bg-blue-700 text-white w-full"
              data-testid="ebay-save-draft-btn"
            >
              Save as Draft on eBay
            </Button>
          )}
        </div>
      )}

      {/* Save Draft Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white" data-testid="ebay-save-dialog">
          <DialogHeader>
            <DialogTitle>Save as Draft on eBay</DialogTitle>
            <DialogDescription>
              Pick the category, policies, and price. We'll push the saved title,
              description, and images straight into your eBay Seller Hub as a draft —
              you can review and publish it from eBay.
            </DialogDescription>
          </DialogHeader>

          {/* Category */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">Category</label>

            {/* Quick picks — one click for common jewelry types */}
            {!categoryId && (
              <div className="space-y-1.5">
                <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-medium">Quick picks</div>
                <div className="flex flex-wrap gap-1.5">
                  {QUICK_CATEGORIES.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => pickQuickCategory(c)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-zinc-50 hover:bg-blue-50 hover:text-blue-700 border border-zinc-200 hover:border-blue-200 text-[11px] text-zinc-700 transition-colors"
                      data-testid={`ebay-quick-cat-${c.id}`}
                      title={c.breadcrumb}
                    >
                      {c.name}
                      <span className="text-[9px] font-mono text-zinc-400">{c.id}</span>
                    </button>
                  ))}
                </div>
                <div className="text-[10px] text-zinc-400 pt-1">Or search eBay's full taxonomy below.</div>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Input
                placeholder="e.g. engagement ring"
                value={categoryQuery}
                onChange={(e) => setCategoryQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchCategory()}
                className="text-sm"
                data-testid="ebay-category-query"
              />
              <Button
                variant="outline" size="sm"
                onClick={handleSearchCategory}
                disabled={searchingCategory || categoryQuery.trim().length < 2}
                data-testid="ebay-category-search-btn"
              >
                {searchingCategory
                  ? <Loader2 size={13} className="animate-spin" />
                  : <><Search size={13} className="mr-1" /> Search</>}
              </Button>
            </div>
            {categoryId && (
              <div className="bg-emerald-50/40 border border-emerald-100 rounded-md px-2.5 py-1.5 text-[12px] text-emerald-900 flex items-center justify-between gap-2">
                <span className="truncate">
                  <span className="font-mono text-emerald-700">[{categoryId}]</span>
                  {' '}
                  {categoryName || product?.ebay_category_name || 'Selected category'}
                </span>
                <button
                  onClick={() => { setCategoryId(''); setCategoryName(''); }}
                  className="text-emerald-700 hover:text-red-600 flex-shrink-0"
                >
                  <X size={13} />
                </button>
              </div>
            )}
            {categorySuggestions.length > 0 && !categoryId && (
              <div className="border border-zinc-100 rounded-md divide-y divide-zinc-100 max-h-44 overflow-y-auto">
                {categorySuggestions.map((s) => (
                  <button
                    key={s.category_id}
                    onClick={() => { setCategoryId(s.category_id); setCategoryName(s.category_name); setCategorySuggestions([]); }}
                    className="w-full text-left px-3 py-2 hover:bg-zinc-50 text-[12px] flex items-center justify-between gap-2"
                    data-testid={`ebay-category-suggestion-${s.category_id}`}
                  >
                    <span className="text-zinc-900">{s.category_name}</span>
                    <span className="text-[10px] font-mono text-zinc-400">{s.category_id}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Policies */}
          <div className="space-y-3">
            <label className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">Business Policies</label>
            {loadingPolicies && (
              <div className="text-xs text-zinc-500 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading your policies…</div>
            )}
            {policiesError && (
              <div className="bg-red-50 border border-red-100 rounded-md p-2.5 text-[11px] text-red-900 leading-relaxed flex items-start gap-2">
                <AlertCircle size={13} className="text-red-600 mt-0.5 flex-shrink-0" />
                <span>{policiesError}</span>
              </div>
            )}
            {policies && (
              <div className="grid grid-cols-1 gap-3">
                <PolicyPicker
                  label="Payment"
                  options={policies.payment}
                  value={paymentPolicyId}
                  onChange={setPaymentPolicyId}
                  testId="ebay-payment-policy"
                />
                <PolicyPicker
                  label="Shipping / Fulfillment"
                  options={policies.fulfillment}
                  value={fulfillmentPolicyId}
                  onChange={setFulfillmentPolicyId}
                  testId="ebay-fulfillment-policy"
                />
                <PolicyPicker
                  label="Return"
                  options={policies.return}
                  value={returnPolicyId}
                  onChange={setReturnPolicyId}
                  testId="ebay-return-policy"
                />
              </div>
            )}
          </div>

          {/* Listing type tabs — fixed (single SKU) vs full variation */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-3">
            <TabsList className="bg-zinc-100 w-full grid grid-cols-2" data-testid="ebay-save-tabs">
              <TabsTrigger value="fixed" data-testid="ebay-tab-fixed">Fixed - Without Variations</TabsTrigger>
              <TabsTrigger value="variation" data-testid="ebay-tab-variation">With Variations</TabsTrigger>
            </TabsList>

            {/* ===== Fixed tab — single SKU ===== */}
            <TabsContent value="fixed" className="space-y-3">
              {extractedFixed && (Object.keys(extractedFixed).length > 0) && (
                <div className="bg-emerald-50/40 border border-emerald-100 rounded-md p-2 text-[11px] text-emerald-900">
                  Auto-detected from title/description:{' '}
                  {extractedFixed.metal && <span className="font-medium">{extractedFixed.metal}</span>}
                  {extractedFixed.metal_purity && <> · {extractedFixed.metal_purity}</>}
                  {extractedFixed.carat && <> · {extractedFixed.carat} CT</>}
                  <span className="text-emerald-900/70"> — change anything below if wrong.</span>
                </div>
              )}

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[11px] text-zinc-500 mb-1 block uppercase tracking-wider">Metal</label>
                  <select
                    value={fixedMetal}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFixedMetal(v);
                      // Auto-fill purity when picking Silver/Platinum
                      if (v === 'Sterling Silver') setFixedPurity('925');
                      else if (v === 'Platinum') setFixedPurity('950');
                      else if (['Rose Gold', 'White Gold', 'Yellow Gold'].includes(v) && ['925', '950'].includes(fixedPurity)) setFixedPurity('');
                    }}
                    className="w-full text-sm border border-zinc-200 rounded-md px-2 py-1.5 bg-white text-zinc-900"
                    data-testid="ebay-fixed-metal"
                  >
                    <option value="">— select —</option>
                    <option value="Rose Gold">Rose Gold</option>
                    <option value="White Gold">White Gold</option>
                    <option value="Yellow Gold">Yellow Gold</option>
                    <option value="Sterling Silver">Sterling Silver</option>
                    <option value="Platinum">Platinum</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] text-zinc-500 mb-1 block uppercase tracking-wider">Metal Purity</label>
                  <select
                    value={fixedPurity}
                    onChange={(e) => setFixedPurity(e.target.value)}
                    className="w-full text-sm border border-zinc-200 rounded-md px-2 py-1.5 bg-white text-zinc-900"
                    disabled={!fixedMetal}
                    data-testid="ebay-fixed-purity"
                  >
                    <option value="">— select —</option>
                    {['Rose Gold', 'White Gold', 'Yellow Gold'].includes(fixedMetal) ? (
                      <>
                        <option value="10k">10k</option>
                        <option value="14k">14k</option>
                        <option value="18k">18k</option>
                      </>
                    ) : fixedMetal === 'Sterling Silver' ? (
                      <option value="925">925</option>
                    ) : fixedMetal === 'Platinum' ? (
                      <option value="950">950</option>
                    ) : null}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] text-zinc-500 mb-1 block uppercase tracking-wider">Center Stone</label>
                  <select
                    value={fixedCarat}
                    onChange={(e) => setFixedCarat(e.target.value)}
                    className="w-full text-sm border border-zinc-200 rounded-md px-2 py-1.5 bg-white text-zinc-900"
                    data-testid="ebay-fixed-carat"
                  >
                    <option value="">— select —</option>
                    {[1, 1.5, 2, 2.5, 3, 4].map((c) => (
                      <option key={c} value={c}>{c.toFixed(2)} CT</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Price preview / override */}
              <div className="bg-blue-50/40 border border-blue-100 rounded-md p-3 text-[12px] text-blue-900 leading-relaxed">
                {computeFixedPrice() ? (
                  <>
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-blue-900/60 mb-0.5">Listing price (auto)</div>
                        <div className="text-xl font-semibold text-blue-900">
                          ${computeFixedPrice().toLocaleString()}
                        </div>
                      </div>
                      <div className="text-right text-[11px] text-blue-900/70">
                        From your pricing table<br />
                        Side stones: {Number(product?.setting_stone_carat_weight || 0).toFixed(2)} ct
                      </div>
                    </div>
                    <div className="mt-2 pt-2 border-t border-blue-100">
                      <label className="text-[10px] text-blue-900/60 uppercase tracking-wider">Override price (optional)</label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Leave blank to use auto-price"
                        value={fixedPriceOverride}
                        onChange={(e) => setFixedPriceOverride(e.target.value)}
                        className="text-sm mt-1 bg-white"
                        data-testid="ebay-fixed-price-override"
                      />
                    </div>
                  </>
                ) : (
                  <div className="text-blue-900/70">Pick metal, purity, and carat to see the auto-calculated price.</div>
                )}
              </div>
            </TabsContent>

            {/* ===== Variation tab — full 66 ===== */}
            <TabsContent value="variation" className="space-y-3">
              <div className="bg-amber-50/40 border border-amber-100 rounded-md p-2 text-[11px] text-amber-900 leading-snug">
                <span className="font-semibold">Heads up:</span> 66 variations pushes ~$130K against your monthly eBay listing limit. Only use this if you have a high enough limit.
              </div>
              <div className="bg-blue-50/40 border border-blue-100 rounded-md p-3 text-[12px] text-blue-900 leading-relaxed">
                <div className="font-medium mb-1">66 variations will be created</div>
                <div className="text-blue-900/70">
                  Metal (Rose / White / Yellow Gold × 10k / 14k / 18k) + Sterling Silver 925 + Platinum 950
                  <br />
                  × 6 center stone carats (1, 1.5, 2, 2.5, 3, 4 CT)
                  <br />
                  <span className="text-blue-900/60">
                    Prices auto-filled from your eBay pricing table (setting stone {Number(product?.setting_stone_carat_weight || 0).toFixed(2)} ct).
                  </span>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowSaveDialog(false)} data-testid="ebay-save-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={activeTab === 'fixed' ? handleSaveDraftFixed : handleSaveDraft}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="ebay-save-confirm-btn"
            >
              {saving
                ? <><Loader2 size={13} className="mr-1.5 animate-spin" /> Saving…</>
                : (activeTab === 'fixed' ? 'Save as Draft (Fixed)' : 'Save as Draft (66 Variations)')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Location Setup Dialog */}
      <Dialog open={showLocationDialog} onOpenChange={setShowLocationDialog}>
        <DialogContent className="max-w-md bg-white" data-testid="ebay-location-dialog">
          <DialogHeader>
            <DialogTitle>Shipping Location</DialogTitle>
            <DialogDescription>
              eBay attaches this address to every listing as the ship-from origin.
              You only set this up once — all future drafts use it automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] text-zinc-500 mb-1 block">Location name</label>
              <Input value={locName} onChange={(e) => setLocName(e.target.value)} placeholder="TLJ Main Warehouse" data-testid="ebay-loc-name" />
            </div>
            <div>
              <label className="text-[11px] text-zinc-500 mb-1 block">Address line 1</label>
              <Input value={locAddress1} onChange={(e) => setLocAddress1(e.target.value)} placeholder="Street + building number" data-testid="ebay-loc-addr" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] text-zinc-500 mb-1 block">City</label>
                <Input value={locCity} onChange={(e) => setLocCity(e.target.value)} placeholder="Mumbai" data-testid="ebay-loc-city" />
              </div>
              <div>
                <label className="text-[11px] text-zinc-500 mb-1 block">State / province</label>
                <Input value={locState} onChange={(e) => setLocState(e.target.value)} placeholder="Maharashtra" data-testid="ebay-loc-state" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] text-zinc-500 mb-1 block">Postal code</label>
                <Input value={locPostal} onChange={(e) => setLocPostal(e.target.value)} placeholder="400001" data-testid="ebay-loc-postal" />
              </div>
              <div>
                <label className="text-[11px] text-zinc-500 mb-1 block">Country (2-letter)</label>
                <Input value={locCountry} onChange={(e) => setLocCountry(e.target.value.toUpperCase().slice(0, 2))} placeholder="IN" maxLength={2} data-testid="ebay-loc-country" />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowLocationDialog(false)} data-testid="ebay-loc-cancel-btn">Cancel</Button>
            <Button
              onClick={handleSaveLocation}
              disabled={savingLocation}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="ebay-loc-save-btn"
            >
              {savingLocation
                ? <><Loader2 size={13} className="mr-1.5 animate-spin" /> Saving…</>
                : 'Save location'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PolicyPicker({ label, options, value, onChange, testId }) {
  const opts = options || [];
  return (
    <div>
      <div className="text-[11px] text-zinc-500 mb-1">{label}</div>
      {opts.length === 0 ? (
        <div className="text-[11px] text-amber-700 bg-amber-50/40 border border-amber-100 rounded-md p-2">
          No {label.toLowerCase()} policies found. Create one in eBay Business Policies.
        </div>
      ) : (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full text-sm border border-zinc-200 rounded-md px-2.5 py-2 bg-white text-zinc-900"
          data-testid={testId}
        >
          <option value="">— Select {label.toLowerCase()} policy —</option>
          {opts.map((p) => (
            <option key={p.policy_id} value={p.policy_id}>{p.name}</option>
          ))}
        </select>
      )}
    </div>
  );
}
